/**
 * Pure geometry for the Bahelakotak custom-crop feature.
 *
 * A kotak booth crops each of its two live feeds to the exact rectangle of a physical
 * box window. Because the two cameras never sit in identical positions, the crop is an
 * ARBITRARY rectangle (offset + size), not the centered square the generic capture path
 * produces. We persist it as a normalized rect in UNROTATED SENSOR space so the same
 * value drives both the low-res live preview and the full-res still (resolution-free), and
 * matches `createImageBitmap(blob, sx, sy, sw, sh)` / `processPreview` source-rect semantics.
 *
 *   sensor space:  the raw camera bitmap BEFORE Preview's CSS previewRotation + mirror.
 *   display space: what the operator actually sees on screen (AFTER rotation + mirror).
 *
 * The admin editor lets the operator drag a box in DISPLAY space; these helpers convert
 * that to the sensor rect we store (and back), accounting for previewRotation ∈ {0,90,180,270}
 * and the "visual horizontal flip" mirror (see imageUtils.getRotationAwareMirrorTransform).
 */

export interface CropRect {
  /** All normalized to [0,1]. x/y = top-left, w/h = size, in unrotated sensor space. */
  x: number
  y: number
  w: number
  h: number
}

export type PreviewRotation = 0 | 90 | 180 | 270

/** Smallest crop side we allow, as a fraction of the sensor — keeps the box grabbable. */
const MIN_SIDE = 0.05

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

/** Clamp a rect into [0,1]², enforcing a minimum side and keeping it fully in-bounds. */
export function clampRect(r: CropRect): CropRect {
  const w = Math.min(1, Math.max(MIN_SIDE, r.w))
  const h = Math.min(1, Math.max(MIN_SIDE, r.h))
  const x = Math.min(1 - w, Math.max(0, r.x))
  const y = Math.min(1 - h, Math.max(0, r.y))
  return { x, y, w, h }
}

function rectAspectValid(r: CropRect): boolean {
  return (
    Number.isFinite(r.x) &&
    Number.isFinite(r.y) &&
    Number.isFinite(r.w) &&
    Number.isFinite(r.h) &&
    r.w > 0 &&
    r.h > 0
  )
}

/**
 * The sensor-pixel aspect (w/h) a crop must have so that, AFTER previewRotation, the final
 * saved/displayed photo reads as `finalRatio` (w/h). For 90/270° the rotation swaps the
 * axes, so the sensor rectangle is the inverse of the on-screen ratio.
 */
export function sensorPixelAspectForFinalRatio(
  finalRatio: number,
  rotation: PreviewRotation
): number {
  const rotated = rotation === 90 || rotation === 270
  return rotated ? 1 / finalRatio : finalRatio
}

/**
 * Largest centered crop of the given final display ratio that fits the sensor. Used as the
 * default when no rect has been saved yet — for finalRatio = 1 (the kotak default) on any
 * sensor this reproduces the legacy centered-square crop exactly, so existing booths are
 * visually unchanged until an operator adjusts.
 */
export function centeredSensorRect(
  finalRatio: number,
  sensorW: number,
  sensorH: number,
  rotation: PreviewRotation
): CropRect {
  if (!(finalRatio > 0) || !(sensorW > 0) || !(sensorH > 0)) {
    return { x: 0, y: 0, w: 1, h: 1 }
  }
  const a = sensorPixelAspectForFinalRatio(finalRatio, rotation) // desired sensor w/h in pixels
  const sensorAspect = sensorW / sensorH
  let w: number
  let h: number
  if (a >= sensorAspect) {
    // Crop spans full sensor width; height is the limiting term.
    w = 1
    h = sensorAspect / a
  } else {
    // Crop spans full sensor height; width is the limiting term.
    h = 1
    w = a / sensorAspect
  }
  return clampRect({ x: (1 - w) / 2, y: (1 - h) / 2, w, h })
}

/**
 * Re-fit an existing rect to a new final ratio, keeping its CENTER and (where it fits) its
 * width, then clamping in-bounds. Used when the operator changes the shared ratio: each
 * pane keeps roughly where/how big it was rather than snapping back to a centered default.
 */
export function refitRectToRatio(
  rect: CropRect,
  finalRatio: number,
  sensorW: number,
  sensorH: number,
  rotation: PreviewRotation
): CropRect {
  if (!rectAspectValid(rect) || !(sensorW > 0) || !(sensorH > 0)) {
    return centeredSensorRect(finalRatio, sensorW, sensorH, rotation)
  }
  const a = sensorPixelAspectForFinalRatio(finalRatio, rotation)
  const sensorAspect = sensorW / sensorH
  const cx = rect.x + rect.w / 2
  const cy = rect.y + rect.h / 2

  // Keep current width in sensor-normalized units; derive height so the sensor PIXEL aspect
  // (w*sensorW)/(h*sensorH) === a. If the derived height overflows, shrink both to fit.
  let w = rect.w
  let h = (w * sensorAspect) / a
  if (h > 1) {
    h = 1
    w = (a * h) / sensorAspect
  }
  if (w > 1) {
    w = 1
    h = (w * sensorAspect) / a
  }
  return clampRect({ x: cx - w / 2, y: cy - h / 2, w, h })
}

// ── Display ⇄ sensor rect mapping ──────────────────────────────────────────────────────────
// Rotations are 90° multiples, so an axis-aligned box stays axis-aligned; mapping the two
// opposite corners and taking min/max is exact. Mirror is modeled as its documented NET
// effect: a horizontal flip in DISPLAY space (getRotationAwareMirrorTransform is built so the
// visual result is a horizontal flip for every rotation angle).

/** display (post-rotation, pre-mirror) normalized point → sensor normalized point. */
function displayPointToSensor(dx: number, dy: number, rotation: PreviewRotation): [number, number] {
  switch (rotation) {
    case 90:
      return [dy, 1 - dx]
    case 180:
      return [1 - dx, 1 - dy]
    case 270:
      return [1 - dy, dx]
    case 0:
    default:
      return [dx, dy]
  }
}

/** sensor normalized point → display (post-rotation, pre-mirror) normalized point. */
function sensorPointToDisplay(u: number, v: number, rotation: PreviewRotation): [number, number] {
  switch (rotation) {
    case 90:
      return [1 - v, u]
    case 180:
      return [1 - u, 1 - v]
    case 270:
      return [v, 1 - u]
    case 0:
    default:
      return [u, v]
  }
}

function rectFromCorners(ax: number, ay: number, bx: number, by: number): CropRect {
  const x = Math.min(ax, bx)
  const y = Math.min(ay, by)
  return { x, y, w: Math.abs(bx - ax), h: Math.abs(by - ay) }
}

/**
 * Convert a rect the operator drew in DISPLAY space (what they see, mirror included) into the
 * unrotated SENSOR rect we persist and feed to the crop pipeline.
 */
export function displayRectToSensorRect(
  displayRect: CropRect,
  rotation: PreviewRotation,
  mirror: boolean
): CropRect {
  let d = displayRect
  if (mirror) {
    // Undo the visual horizontal flip before applying the inverse rotation.
    d = { ...d, x: 1 - (d.x + d.w) }
  }
  const [u1, v1] = displayPointToSensor(d.x, d.y, rotation)
  const [u2, v2] = displayPointToSensor(d.x + d.w, d.y + d.h, rotation)
  return clampRect(rectFromCorners(u1, v1, u2, v2))
}

/**
 * Inverse of {@link displayRectToSensorRect}: sensor rect → the DISPLAY rect to render the
 * editor box at (so the box sits over the exact same pixels the operator originally framed).
 */
export function sensorRectToDisplayRect(
  sensorRect: CropRect,
  rotation: PreviewRotation,
  mirror: boolean
): CropRect {
  const [d1x, d1y] = sensorPointToDisplay(sensorRect.x, sensorRect.y, rotation)
  const [d2x, d2y] = sensorPointToDisplay(sensorRect.x + sensorRect.w, sensorRect.y + sensorRect.h, rotation)
  let d = rectFromCorners(d1x, d1y, d2x, d2y)
  if (mirror) {
    d = { ...d, x: 1 - (d.x + d.w) }
  }
  return d
}

/** Aspect (w/h) of the image AS DISPLAYED, given the raw sensor size and rotation. */
export function displayAspect(sensorW: number, sensorH: number, rotation: PreviewRotation): number {
  const rotated = rotation === 90 || rotation === 270
  const a = sensorW / sensorH
  return rotated ? 1 / a : a
}

/** Normalize any previewRotation-ish number to the {0,90,180,270} union. */
export function normalizeRotation(rotation: number | undefined): PreviewRotation {
  const r = (((rotation ?? 0) % 360) + 360) % 360
  return r === 90 || r === 180 || r === 270 ? r : 0
}
